import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import PaywallModal from '../components/PaywallModal';
import { Helmet } from 'react-helmet-async';
import { Lock, Unlock, ArrowRight, Clock, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUTTON_CONFIG = [
  { label: 'C',  pack: 'ultimate_math', type: 'clear',    display: 'C',  wide: false },
  { label: '(',  pack: 'ultimate_math', type: 'bracket',  display: '(',  wide: false },
  { label: ')',  pack: 'ultimate_math', type: 'bracket',  display: ')',  wide: false },
  { label: '/',  pack: 'advanced_ops', type: 'operator', display: '÷',  wide: false },
  { label: '7',  pack: 'advanced_ops', type: 'number',   display: '7',  wide: false },
  { label: '8',  pack: 'advanced_ops', type: 'number',   display: '8',  wide: false },
  { label: '9',  pack: 'advanced_ops', type: 'number',   display: '9',  wide: false },
  { label: '*',  pack: 'advanced_ops', type: 'operator', display: '×',  wide: false },
  { label: '4',  pack: 'basic_math',   type: 'number',   display: '4',  wide: false },
  { label: '5',  pack: 'basic_math',   type: 'number',   display: '5',  wide: false },
  { label: '6',  pack: 'advanced_ops', type: 'number',   display: '6',  wide: false },
  { label: '-',  pack: 'basic_math',   type: 'operator', display: '−',  wide: false },
  { label: '1',  pack: 'basic_math',   type: 'number',   display: '1',  wide: false },
  { label: '2',  pack: 'basic_math',   type: 'number',   display: '2',  wide: false },
  { label: '3',  pack: 'basic_math',   type: 'number',   display: '3',  wide: false },
  { label: '+',  pack: 'basic_math',   type: 'operator', display: '+',  wide: false },
  { label: '0',  pack: null,           type: 'number',   display: '0',  wide: true  },
  { label: '.',  pack: 'advanced_ops', type: 'decimal',  display: '.',  wide: false },
  { label: '=',  pack: 'ultimate_math',type: 'equals',   display: '=',  wide: false },
];

const safeEval = (expr) => {
  try {
    const s = expr.replace(/[^0-9+\-*/().]/g, '');
    // eslint-disable-next-line no-new-func
    const r = new Function('"use strict"; return (' + s + ')')();
    if (typeof r !== 'number' || !isFinite(r)) return 'ERROR';
    return parseFloat(r.toFixed(10)).toString();
  } catch { return 'ERROR'; }
};

// ── Sound Effects via Web Audio API ──────────────────────────────────────────
const audioCtxRef = { current: null };
function getAudioCtx() {
  if (!audioCtxRef.current) {
    audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioCtxRef.current;
}
function playTone(freq, duration = 0.1, type = 'sine') {
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = type;
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + duration);
  } catch {}
}

export default function CalculatorPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unlockedPacks, setUnlockedPacks] = useState([]);
  const [display, setDisplay] = useState('0');
  const [expression, setExpression] = useState('');
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallPack, setPaywallPack] = useState('basic_math');
  const [shakingBtn, setShakingBtn] = useState(null);
  const [justCalculated, setJustCalculated] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Add-on toggle state — persisted in localStorage, independent of ownership
  const [addOnEnabled, setAddOnEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem('calcbyea_addon_toggles');
      return saved ? JSON.parse(saved) : { dark_mode_skin: true, sound_effects: true, history_log: true };
    } catch { return { dark_mode_skin: true, sound_effects: true, history_log: true }; }
  });
  const toggleAddOn = useCallback((id) => {
    setAddOnEnabled(prev => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem('calcbyea_addon_toggles', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const ownsDark    = unlockedPacks.includes('dark_mode_skin');
  const ownsSounds  = unlockedPacks.includes('sound_effects');
  const ownsHistory = unlockedPacks.includes('history_log');

  const hasDark    = ownsDark    && (addOnEnabled.dark_mode_skin    ?? true);
  const hasSounds  = ownsSounds  && (addOnEnabled.sound_effects     ?? true);
  const hasHistory = ownsHistory && (addOnEnabled.history_log       ?? true);
  const hasNegativeNumbers = unlockedPacks.includes('negative_numbers') || unlockedPacks.includes('ultimate_math');

  const CORE_PACKS = ['basic_math', 'advanced_ops', 'ultimate_math'];
  const coreUnlocked = CORE_PACKS.filter(p => unlockedPacks.includes(p)).length;

  useEffect(() => { if (user) setUnlockedPacks(user.unlocked_packs || []); }, [user]);
  useEffect(() => {
    if (!user) return;
    fetch(`${API}/user/unlocks`, { credentials: 'include' })
      .then(r => r.json()).then(d => setUnlockedPacks(d.unlocked_packs || [])).catch(() => {});
  }, [user]);

  const isUnlocked = useCallback((btn) => !btn.pack || unlockedPacks.includes(btn.pack), [unlockedPacks]);

  const playSound = useCallback((btnType) => {
    if (!hasSounds) return;
    if (btnType === 'equals')   { playTone(880, 0.08); setTimeout(() => playTone(1100, 0.12), 60); }
    else if (btnType === 'operator') { playTone(660, 0.09, 'triangle'); }
    else if (btnType === 'clear')    { playTone(330, 0.12, 'sawtooth'); }
    else                             { playTone(440, 0.07); }
  }, [hasSounds]);

  const handleButtonClick = (btn) => {
    if (!isUnlocked(btn)) {
      setShakingBtn(btn.label);
      setTimeout(() => setShakingBtn(null), 500);
      setPaywallPack(btn.pack);
      setPaywallOpen(true);
      return;
    }
    playSound(btn.type);
    if (btn.type === 'clear') { setDisplay('0'); setExpression(''); setJustCalculated(false); return; }
    if (btn.type === 'equals') {
      if (!expression) return;
      const r = safeEval(expression);
      // Gate negative results — clamp to '0' if user doesn't own the Negative Numbers pack
      const gatedResult = r !== 'ERROR' && parseFloat(r) < 0 && !hasNegativeNumbers ? '0' : r;
      if (gatedResult !== 'ERROR' && hasHistory) {
        setHistory(prev => [{ expr: expression, result: gatedResult, ts: Date.now() }, ...prev].slice(0, 100));
      }
      setDisplay(gatedResult); setExpression(gatedResult === 'ERROR' ? '' : gatedResult); setJustCalculated(true); return;
    }
    if (btn.type === 'operator' || btn.type === 'bracket') {
      setExpression(expression + btn.label); setDisplay(btn.display); setJustCalculated(false); return;
    }
    let newExpr;
    if (justCalculated && btn.type === 'number') { newExpr = btn.label; setJustCalculated(false); }
    else { newExpr = (expression === '0' ? '' : expression) + btn.label; }
    setExpression(newExpr);
    const last = newExpr.split(/[+\-*/()]/).at(-1);
    setDisplay(last || btn.display);
  };

  const handleBackspace = () => {
    if (!expression || expression.length === 0) { setDisplay('0'); return; }
    const newExpr = expression.slice(0, -1);
    setExpression(newExpr);
    if (!newExpr) { setDisplay('0'); return; }
    const last = newExpr.split(/[+\-*/()]/).at(-1);
    setDisplay(last || newExpr.slice(-1) || '0');
    setJustCalculated(false);
  };

  // Format expression to show spaces around operators for readability
  const formatExpression = (expr) => expr.replace(/([+\-*/])/g, ' $1 ');

  // ── Dynamic classes driven by dark mode skin ──────────────────────────────
  const dk = hasDark;
  const calcBg   = dk ? 'bg-zinc-900 border-zinc-700 shadow-[8px_8px_0px_0px_rgba(9,9,11,1)]' : 'bg-white border-zinc-950 shadow-[8px_8px_0px_0px_rgba(9,9,11,1)]';
  const dispBg   = dk ? 'bg-zinc-950'       : 'bg-zinc-950';
  const exprColor= dk ? 'text-zinc-500'     : 'text-zinc-600';
  const gridBorder = dk ? 'border-zinc-700' : 'border-zinc-200';
  const footerBg  = dk ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-100';
  const footerText = dk ? 'text-zinc-600' : 'text-zinc-300';

  const getButtonClass = (btn) => {
    const u = isUnlocked(btn);
    const border = `border-b border-r ${dk ? 'border-zinc-700' : 'border-zinc-200'}`;
    const base = `relative h-14 sm:h-16 flex items-center justify-center select-none cursor-pointer
      font-bebas text-xl sm:text-2xl ${border}
      transition-all duration-75 ease-in-out`;

    // ── free "0" key ─────────────────────────────────────────────────────────
    if (!btn.pack) return `${base} text-3xl bg-zinc-950 text-white
      hover:bg-zinc-700 active:bg-zinc-800 active:scale-95`;

    // ── locked keys ──────────────────────────────────────────────────────────
    if (!u) {
      if (btn.type === 'operator' || btn.type === 'equals') {
        return `${base}
          ${dk ? 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600 hover:text-white'
                : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200 hover:text-zinc-950'}
          active:scale-95`;
      }
      // numbers / brackets / clear / decimal — visible but clearly "locked"
      return `${base}
        ${dk ? 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-100'
              : 'bg-white text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950'}
        active:scale-95`;
    }

    // ── equals ───────────────────────────────────────────────────────────────
    if (btn.type === 'equals') return `${base} bg-zinc-950 text-white
      hover:bg-zinc-700 active:bg-zinc-600 active:scale-95`;

    // ── operators ────────────────────────────────────────────────────────────
    if (btn.type === 'operator') return `${base}
      ${dk ? 'bg-zinc-700 text-white hover:bg-zinc-600 active:bg-zinc-500'
            : 'bg-zinc-950 text-white hover:bg-zinc-700 active:bg-zinc-600'}
      active:scale-95`;

    // ── clear ────────────────────────────────────────────────────────────────
    if (btn.type === 'clear') return `${base}
      ${dk ? 'bg-zinc-700 text-zinc-100 hover:bg-zinc-600 active:bg-zinc-500'
            : 'bg-zinc-100 text-zinc-950 hover:bg-zinc-200 active:bg-zinc-300'}
      active:scale-95`;

    // ── numbers / brackets / decimal ─────────────────────────────────────────
    return `${base}
      ${dk ? 'bg-zinc-800 text-zinc-100 hover:bg-zinc-700 active:bg-zinc-600'
            : 'bg-white text-zinc-950 hover:bg-zinc-100 active:bg-zinc-200'}
      active:scale-95`;
  };

  return (
    <div className={`min-h-screen ${dk ? 'bg-zinc-950 text-white' : 'bg-zinc-50 text-zinc-950'}`}>
      <Helmet>
        <title>The Calculator — Unlock Math Operations | CalcByEA™</title>
        <meta name="description" content="The world's only paywalled calculator. Zero is free. Addition costs $0.99. The equals sign costs $9.99. Buy DLC packs to unlock operations." />
        <link rel="canonical" href="https://calculatorbyea.com/calculator" />
        <meta property="og:url" content="https://calculatorbyea.com/calculator" />
        <meta property="og:title" content="The Calculator — Unlock Math Operations | CalcByEA™" />
        <meta property="og:description" content="The world's only paywalled calculator. Zero is free. Addition costs $0.99. The equals sign costs $9.99. Buy DLC packs to unlock operations." />
        <meta name="twitter:title" content="The Calculator — Unlock Math Operations | CalcByEA™" />
        <meta name="twitter:description" content="Zero is free. Everything else costs money. The world's only paywalled calculator." />
      </Helmet>
      <Navbar />

      <div className="pt-24 pb-12 min-h-screen flex flex-col items-center justify-center px-3 sm:px-4">

        {/* Header */}
        <div className="w-full max-w-sm mb-5">
          <div className={`text-[9px] font-bold uppercase tracking-[0.35em] mb-1 ${dk ? 'text-zinc-500' : 'text-zinc-400'}`}>
            Premium Math Experience™
            {hasDark && <span className="ml-2 border border-zinc-600 px-1.5 py-0.5 text-zinc-500">DARK SKIN</span>}
          </div>
          <h1 className={`font-bebas text-3xl ${dk ? 'text-white' : 'text-zinc-950'}`}>The Calculator</h1>
          <p className={`text-[11px] font-semibold uppercase tracking-widest mt-1 ${dk ? 'text-zinc-500' : 'text-zinc-400'}`}>
          {user ? (coreUnlocked === 0 ? 'Unlock your first pack to begin' : `${coreUnlocked}/3 core packs unlocked`) : 'Login required to purchase packs'}
          </p>
        </div>

        {/* Calculator container */}
        <div className={`w-full max-w-sm border-2 ${calcBg}`}>

          {/* Display */}
          <div className={`${dispBg} px-5 pt-4 pb-6 relative`}>
            <button
              onClick={handleBackspace}
              className="absolute top-3 right-4 text-zinc-600 hover:text-zinc-300 transition-colors duration-100 text-lg leading-none px-1"
              data-testid="calc-backspace"
              title="Delete last"
            >⌫</button>
            <div className={`${exprColor} text-sm font-mono mb-3 text-right pr-6 truncate`}>
              {expression ? formatExpression(expression) : '\u00a0'}
            </div>
            <div className="font-bebas text-5xl text-right leading-none pb-1" data-testid="calc-display">
              {hasNegativeNumbers && display.startsWith('-')
                ? <span className="flex items-center justify-end gap-1.5">
                    <span className="text-red-400 text-sm font-mono border border-red-900/40 bg-red-950/20 px-1.5 py-0.5 leading-none">−</span>
                    <span className="text-white">{display.slice(1)}</span>
                  </span>
                : <span className="text-white">{display}</span>}
            </div>
          </div>

        {/* Pack status strip — clickable, opens paywall */}
          <div className={`grid grid-cols-3 border-b ${dk ? 'border-zinc-700' : 'border-zinc-200'}`}>
            {[
              { id: 'basic_math',    label: 'Basic',    price: '$0.99' },
              { id: 'advanced_ops',  label: 'Adv',      price: '$2.99' },
              { id: 'ultimate_math', label: 'Ult',      price: '$9.99' },
            ].map(({ id, label, price }, i) => {
              const owned = unlockedPacks.includes(id);
              return (
                <button
                  key={id}
                  onClick={() => { if (!owned) { setPaywallPack(id); setPaywallOpen(true); } }}
                  disabled={owned}
                  data-testid={`pack-strip-${id}`}
                  className={`flex flex-col items-center justify-center py-3 gap-0.5 transition-all duration-100
                    ${i < 2 ? `border-r ${dk ? 'border-zinc-700' : 'border-zinc-200'}` : ''}
                    ${owned
                      ? (dk ? 'bg-zinc-800 cursor-default' : 'bg-zinc-950 cursor-default')
                      : (dk
                          ? 'bg-zinc-900 cursor-pointer hover:bg-zinc-700 active:bg-zinc-600'
                          : 'bg-white cursor-pointer hover:bg-zinc-950 group active:bg-zinc-800')
                    }`}
                >
                  {owned
                    ? <Unlock size={9} strokeWidth={3} className={dk ? 'text-zinc-400' : 'text-white'} />
                    : <Lock    size={9} strokeWidth={3} className={`transition-colors duration-100 ${dk ? 'text-zinc-600 group-hover:text-zinc-300' : 'text-zinc-400 group-hover:text-white'}`} />
                  }
                  <span className={`text-[9px] font-bold uppercase tracking-widest leading-none transition-colors duration-100
                    ${owned
                      ? (dk ? 'text-zinc-300' : 'text-white')
                      : (dk ? 'text-zinc-500 group-hover:text-zinc-200' : 'text-zinc-500 group-hover:text-white')
                    }`}>
                    {owned ? label : price}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Button grid */}
          <div className={`grid grid-cols-4 border-t border-l ${gridBorder}`}>
            {BUTTON_CONFIG.map((btn) => (
              <button
                key={btn.label}
                onClick={() => handleButtonClick(btn)}
                data-testid={`calc-btn-${btn.label.replace('*','mul').replace('/','div').replace('+','add').replace('-','sub').replace('(','lp').replace(')','rp').replace('.','dot').replace('=','eq').replace('C','clear').replace('0','zero')}`}
                className={`${getButtonClass(btn)} ${btn.wide ? 'col-span-2' : ''} ${shakingBtn === btn.label ? 'animate-shake' : ''}`}
              >
                {!isUnlocked(btn) && <Lock size={8} strokeWidth={3} className={`absolute top-1.5 right-1.5 ${dk ? 'text-zinc-600' : 'text-zinc-300'}`} />}
                {btn.display}
              </button>
            ))}
          </div>

          <div className={`border-t px-4 py-2.5 ${footerBg}`}>
            <p className={`text-[9px] font-bold uppercase tracking-widest text-center ${footerText}`}>
              The number 0 is always free. EA would never lock zero. Probably.
            </p>
          </div>
        </div>

        {/* Action row */}
        <div className="mt-4 flex gap-3 w-full max-w-sm">
          {unlockedPacks.length < 3 && (
            <button
              onClick={() => navigate('/store')}
              className={`flex-1 flex items-center justify-center gap-2 border-2 text-xs font-bold uppercase tracking-widest px-4 py-3 transition-all duration-100 ${dk ? 'border-zinc-700 text-zinc-500 hover:border-zinc-400 hover:text-white' : 'border-zinc-300 text-zinc-400 hover:border-zinc-950 hover:text-zinc-950'}`}
              data-testid="calculator-store-cta"
            >
              <Unlock size={11} strokeWidth={3} />
              Unlock in DLC Store
              <ArrowRight size={11} strokeWidth={3} />
            </button>
          )}
          {hasHistory && (
            <button
              onClick={() => setHistoryOpen(o => !o)}
              className={`flex items-center gap-2 border-2 text-xs font-bold uppercase tracking-widest px-4 py-3 transition-all duration-100 ${dk ? 'border-zinc-700 text-zinc-500 hover:border-zinc-400 hover:text-white' : 'border-zinc-300 text-zinc-400 hover:border-zinc-950 hover:text-zinc-950'}`}
              data-testid="history-toggle-btn"
            >
              <Clock size={11} strokeWidth={3} />
              History
              <ChevronDown size={11} strokeWidth={3} className={`transition-transform duration-200 ${historyOpen ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>

        {/* History Panel */}
        {hasHistory && historyOpen && (
          <div className={`w-full max-w-sm mt-1 border-2 ${dk ? 'border-zinc-700 bg-zinc-900' : 'border-zinc-200 bg-white'}`} data-testid="history-panel">
            <div className={`flex items-center justify-between px-4 py-2.5 border-b ${dk ? 'border-zinc-700' : 'border-zinc-100'}`}>
              <span className={`text-[9px] font-bold uppercase tracking-[0.3em] ${dk ? 'text-zinc-500' : 'text-zinc-400'}`}>
                Calculation History™ ({history.length}/100)
              </span>
              <button onClick={() => setHistory([])} className={`text-[9px] font-bold uppercase tracking-widest ${dk ? 'text-zinc-600 hover:text-zinc-300' : 'text-zinc-300 hover:text-zinc-950'} transition-colors`} data-testid="history-clear-btn">
                Clear
              </button>
            </div>
            {history.length === 0 ? (
              <div className={`px-4 py-6 text-center text-[10px] font-bold uppercase tracking-widest ${dk ? 'text-zinc-600' : 'text-zinc-300'}`}>
                No calculations yet. Start mashing.
              </div>
            ) : (
              <div className="max-h-48 overflow-y-auto">
                {history.map((item, i) => (
                  <div key={item.ts} className={`flex items-center justify-between px-4 py-2 border-b last:border-b-0 ${dk ? 'border-zinc-800' : 'border-zinc-50'}`} data-testid={`history-item-${i}`}>
                    <span className={`text-xs font-mono ${dk ? 'text-zinc-500' : 'text-zinc-400'}`}>{item.expr}</span>
                    <span className={`font-bebas text-xl ${dk ? 'text-white' : 'text-zinc-950'}`}>= {item.result}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Add-on toggles — click to enable/disable without losing ownership */}
        {(ownsDark || ownsSounds || ownsHistory) && (
          <div className="mt-3 flex flex-wrap gap-2 w-full max-w-sm">
            {ownsDark && (
              <button
                onClick={() => toggleAddOn('dark_mode_skin')}
                className={`text-[9px] font-bold uppercase tracking-widest border px-2.5 py-0.5 transition-all duration-100 ${
                  hasDark
                    ? (dk ? 'border-zinc-500 bg-zinc-700 text-zinc-200' : 'border-zinc-950 bg-zinc-950 text-white')
                    : (dk ? 'border-zinc-800 text-zinc-600' : 'border-zinc-200 text-zinc-400')
                }`}
                data-testid="toggle-dark-mode"
                title="Click to toggle Dark Mode Skin"
              >Dark Skin · {hasDark ? 'ON' : 'OFF'}</button>
            )}
            {ownsSounds && (
              <button
                onClick={() => toggleAddOn('sound_effects')}
                className={`text-[9px] font-bold uppercase tracking-widest border px-2.5 py-0.5 transition-all duration-100 ${
                  hasSounds
                    ? (dk ? 'border-zinc-500 bg-zinc-700 text-zinc-200' : 'border-zinc-950 bg-zinc-950 text-white')
                    : (dk ? 'border-zinc-800 text-zinc-600' : 'border-zinc-200 text-zinc-400')
                }`}
                data-testid="toggle-sounds"
                title="Click to toggle Sound Effects"
              >Sounds · {hasSounds ? 'ON' : 'OFF'}</button>
            )}
            {ownsHistory && (
              <button
                onClick={() => toggleAddOn('history_log')}
                className={`text-[9px] font-bold uppercase tracking-widest border px-2.5 py-0.5 transition-all duration-100 ${
                  hasHistory
                    ? (dk ? 'border-zinc-500 bg-zinc-700 text-zinc-200' : 'border-zinc-950 bg-zinc-950 text-white')
                    : (dk ? 'border-zinc-800 text-zinc-600' : 'border-zinc-200 text-zinc-400')
                }`}
                data-testid="toggle-history"
                title="Click to toggle History Log"
              >History · {hasHistory ? 'ON' : 'OFF'}</button>
            )}
          </div>
        )}
      </div>

      <PaywallModal isOpen={paywallOpen} onClose={() => setPaywallOpen(false)} packId={paywallPack} />
    </div>
  );
}
